import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { templateService } from '../services/templateService'
import { patientService, extractPatients } from '../services/patientService'
import { PatientSelectorModal } from '../components/PatientSelectorModal'
import { TemplateVariableManager } from '../components/TemplateVariableManager'
import { TemplatePreviewModal } from '../components/TemplatePreviewModal'
import './TemplateDetail.css'

function formatCategoryName(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1)
}

export function TemplateDetail() {
  const { id } = useParams<{ id: string }>()
  const [showPatientSelector, setShowPatientSelector] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  const { data: template, isLoading, error } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id!),
    enabled: !!id,
  })

  const { data: patientsData } = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const result = await patientService.getAll()
      return extractPatients(result)
    },
  })
  const patients = patientsData || []

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Template ID is required')
      // Use first patient as sample data, or create mock data
      const samplePatient = patients.length > 0 ? patients[0] : null
      return templateService.preview(id, samplePatient ? { patientId: samplePatient.id } : undefined)
    },
    onSuccess: (blob) => {
      // Show preview in modal instead of downloading
      setPreviewBlob(blob)
      setShowPreviewModal(true)
      setIsPreviewing(false)
    },
    onError: (error) => {
      console.error('Preview error:', error)
      alert('Failed to generate preview: ' + String(error))
      setIsPreviewing(false)
    },
  })

  const handlePreview = () => {
    setIsPreviewing(true)
    previewMutation.mutate()
  }

  if (isLoading) {
    return <div className="template-detail-page">Loading template...</div>
  }

  if (error || !template) {
    return (
      <div className="template-detail-page">
        <p>Error loading template: {String(error)}</p>
        <Link to="/templates" className="btn-secondary">
          Back to Templates
        </Link>
      </div>
    )
  }

  return (
    <div className="template-detail-page">
      <div className="template-detail-header">
        <div>
          <Link to="/templates" className="back-link">
            ← Back to Templates
          </Link>
          <h1>{template.name}</h1>
          <span className={`template-category category-${template.category}`}>
            {formatCategoryName(template.category)}
          </span>
        </div>
        <div className="template-detail-actions">
          <button
            className="btn-secondary"
            onClick={handlePreview}
            disabled={isPreviewing || previewMutation.isPending}
          >
            {isPreviewing || previewMutation.isPending ? 'Generating Preview...' : 'Preview Template'}
          </button>
          <Link to={`/templates/${id}/edit`} className="btn-secondary">
            Edit
          </Link>
          <button 
            className="btn-primary"
            onClick={() => {
              setShowPatientSelector(true)
            }}
          >
            Generate Document
          </button>
        </div>
      </div>

      {template.description && (
        <div className="template-description-section">
          <h2>Description</h2>
          <p>{template.description}</p>
        </div>
      )}

      <div className="template-variables-section">
        <TemplateVariableManager
          templateContent={undefined}
          existingVariables={template.variables || []}
          readOnly={true}
        />
      </div>

      <div className="template-info-section">
        <h2>Template Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Created:</label>
            <span>{new Date(template.createdAt).toLocaleString()}</span>
          </div>
          <div className="info-item">
            <label>Updated:</label>
            <span>{new Date(template.updatedAt).toLocaleString()}</span>
          </div>
          <div className="info-item">
            <label>Status:</label>
            <span className={template.isActive ? 'status-active' : 'status-inactive'}>
              {template.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {showPatientSelector && id && (
        <PatientSelectorModal
          templateId={id}
          onClose={() => {
            setShowPatientSelector(false)
          }}
        />
      )}

      {showPreviewModal && previewBlob && (
        <TemplatePreviewModal
          blob={previewBlob}
          fileName={`preview_${template?.name || 'template'}_${Date.now()}.odt`}
          onClose={() => {
            setShowPreviewModal(false)
            setPreviewBlob(null)
            // Cleanup blob URL
            if (previewBlob) {
              URL.revokeObjectURL(URL.createObjectURL(previewBlob))
            }
          }}
        />
      )}
    </div>
  )
}
































